/**
 * ApiUsagePanel — v49.7. Admin API-usage & quota forecast panel.
 * Displayed in /admin/accounting. Restricted to super_admin + managers server-side.
 * i18n: 4 admin languages (fr, en, es, zh).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gauge, Loader2 } from 'lucide-react';
import { useAdminLang } from '../hooks/useAdminLang';

const L = {
  fr: { title: 'Quotas & API tierces', sub: 'Suivi des consommations pour anticiper les paiements', month: 'Ce mois', period: 'Sur la période', total: 'Total', quota: 'Quota gratuit', usedPct: 'Utilisé', error: 'Erreur', calls: 'appels', minutes: 'min', period_label: 'Période', p_day: 'Jour', p_week: 'Semaine', p_month: 'Mois', p_year: 'Année', p_all: 'Tout', restricted: 'Réservé au super admin + managers', credit: 'Crédit ajouté', save: 'OK', edit: 'Modifier', saved: 'Enregistré' },
  en: { title: 'API quotas & 3rd-party usage', sub: 'Monitor consumption to plan payments', month: 'This month', period: 'Period', total: 'Total', quota: 'Free tier', usedPct: 'Used', error: 'Error', calls: 'calls', minutes: 'min', period_label: 'Period', p_day: 'Day', p_week: 'Week', p_month: 'Month', p_year: 'Year', p_all: 'All', restricted: 'Super admin + managers only', credit: 'Credit paid', save: 'OK', edit: 'Edit', saved: 'Saved' },
  es: { title: 'Cuotas y APIs de terceros', sub: 'Sigue el consumo para anticipar los pagos', month: 'Este mes', period: 'Periodo', total: 'Total', quota: 'Cuota gratis', usedPct: 'Usado', error: 'Error', calls: 'llamadas', minutes: 'min', period_label: 'Periodo', p_day: 'Día', p_week: 'Semana', p_month: 'Mes', p_year: 'Año', p_all: 'Todo', restricted: 'Solo super admin + managers', credit: 'Crédito pagado', save: 'OK', edit: 'Editar', saved: 'Guardado' },
  zh: { title: 'API 配额与第三方用量', sub: '追踪用量以便预测付款', month: '本月', period: '所选时段', total: '累计', quota: '免费配额', usedPct: '已用', error: '错误', calls: '次通话', minutes: '分钟', period_label: '时段', p_day: '日', p_week: '周', p_month: '月', p_year: '年', p_all: '全部', restricted: '仅超级管理员和主管可见', credit: '已充值', save: '确定', edit: '编辑', saved: '已保存' },
};

const CreditEditor = ({ p, l, onSaved }) => {
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(p?.credit?.credit_usd ?? 0);
  const [busy, setBusy] = React.useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await axios.put('/api/admin/api-usage/credit', {
        provider: p.provider, credit_usd: Number(val) || 0, currency: 'USD',
      });
      toast.success(l.saved);
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || l.error);
    } finally { setBusy(false); }
  };
  const c = p?.credit?.credit_usd || 0;
  return (
    <div className="flex items-center gap-1.5 text-[11px]" data-testid={`credit-${p.provider}`}>
      <span className="text-white/50">{l.credit}:</span>
      {editing ? (
        <>
          <input type="number" min="0" step="1" value={val}
                 onChange={e => setVal(e.target.value)}
                 className="w-16 bg-white/10 border border-white/15 rounded px-1.5 py-0.5 text-xs text-white"
                 data-testid={`credit-input-${p.provider}`} />
          <span className="text-white/50">$</span>
          <button onClick={save} disabled={busy}
                  className="px-2 py-0.5 rounded bg-emerald-500/30 text-emerald-200 text-[10px] disabled:opacity-40"
                  data-testid={`credit-save-${p.provider}`}>{l.save}</button>
          <button onClick={() => { setEditing(false); setVal(c); }}
                  className="text-white/40 text-[10px]">✕</button>
        </>
      ) : (
        <>
          <strong className="text-emerald-300">{c > 0 ? `${c.toFixed(2)} $` : '—'}</strong>
          <button onClick={() => setEditing(true)}
                  className="text-cyan-300 underline text-[10px]"
                  data-testid={`credit-edit-${p.provider}`}>{l.edit}</button>
        </>
      )}
    </div>
  );
};

const ApiUsagePanel = () => {
  const { adminLang } = useAdminLang();
  const l = L[adminLang] || L.fr;
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setForbidden(false);
      try {
        const r = await axios.get('/api/admin/api-usage/summary', { params: { period } });
        if (alive) setData(r.data);
      } catch (e) {
        if (e?.response?.status === 403) { setForbidden(true); }
        else toast.error(e?.response?.data?.detail || l.error);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [period, refreshTick]); // eslint-disable-line

  if (forbidden) {
    return (
      <section className="p-4 rounded-3xl bg-white/5 border border-white/10" data-testid="api-usage-panel-forbidden">
        <p className="text-sm text-white/60">🔒 {l.restricted}</p>
      </section>
    );
  }

  return (
    <section className="p-4 rounded-3xl bg-gradient-to-br from-orange-500/10 to-rose-500/10 border border-white/10 space-y-3" data-testid="api-usage-panel">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2"><Gauge className="w-5 h-5 text-orange-300" /> {l.title}</h3>
          <p className="text-xs text-white/60">{l.sub}</p>
        </div>
        <select value={period} onChange={e => setPeriod(e.target.value)} className="bg-white/10 border border-white/15 rounded-lg px-2 py-1.5 text-xs" data-testid="api-usage-period">
          <option value="day"   className="bg-gray-900">{l.p_day}</option>
          <option value="week"  className="bg-gray-900">{l.p_week}</option>
          <option value="month" className="bg-gray-900">{l.p_month}</option>
          <option value="year"  className="bg-gray-900">{l.p_year}</option>
          <option value="all"   className="bg-gray-900">{l.p_all}</option>
        </select>
      </div>

      {loading || !data ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-orange-300" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {(data.providers || []).map(p => (
            <div key={p.provider} className="p-3 rounded-xl bg-white/5 border border-white/10 text-sm space-y-1" data-testid={`api-usage-${p.provider}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-white/90 truncate">{p.label}</p>
                {typeof p.quota_used_percent === 'number' && (
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.quota_used_percent >= 80 ? 'bg-rose-500/30 text-rose-200' : p.quota_used_percent >= 50 ? 'bg-amber-500/30 text-amber-200' : 'bg-emerald-500/25 text-emerald-200'}`}>
                    {l.usedPct} {p.quota_used_percent}%
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/70">
                {p.usage_current_month != null && <span>{l.month}: <strong className="text-white">{p.usage_current_month}</strong>{p.free_tier_month ? ` / ${p.free_tier_month}` : ''}</span>}
                {p.usage_period != null && p.usage_current_month == null && <span>{l.period}: <strong className="text-white">{p.usage_period}</strong></span>}
                {p.usage_period != null && p.usage_current_month != null && <span>{l.period}: <strong className="text-white">{p.usage_period}</strong></span>}
                {p.usage_text_period != null && <span>Text: <strong className="text-white">{p.usage_text_period}</strong></span>}
                {p.usage_image_period != null && <span>Image: <strong className="text-white">{p.usage_image_period}</strong></span>}
                {p.usage_stt_period != null && <span>Whisper: <strong className="text-white">{p.usage_stt_period}</strong></span>}
                {p.usage_tts_period != null && <span>TTS: <strong className="text-white">{p.usage_tts_period}</strong></span>}
                {p.failed_period != null && p.failed_period > 0 && <span className="text-rose-300">Erreurs: <strong>{p.failed_period}</strong></span>}
                {(p.tokens_in_period != null && p.tokens_in_period > 0) && <span>Tokens in: <strong className="text-white">{p.tokens_in_period.toLocaleString()}</strong></span>}
                {(p.tokens_out_period != null && p.tokens_out_period > 0) && <span>Tokens out: <strong className="text-white">{p.tokens_out_period.toLocaleString()}</strong></span>}
                {p.usage_all_time != null && <span>{l.total}: <strong className="text-white">{p.usage_all_time}</strong></span>}
                {p.usage_period_minutes != null && <span>{p.usage_period_minutes} {l.minutes} · {p.usage_period_calls} {l.calls}</span>}
              </div>
              {p.next_pricing_note && <p className="text-[10px] text-white/40 italic">{p.next_pricing_note}</p>}
              {p.credit && ['google_ai','openai','resend','airalo','agora'].includes(p.provider) && (
                <CreditEditor p={p} l={l} onSaved={() => setRefreshTick(t => t + 1)} />
              )}
              {typeof p.quota_used_percent === 'number' && (
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div className={`h-full ${p.quota_used_percent >= 80 ? 'bg-rose-400' : p.quota_used_percent >= 50 ? 'bg-amber-400' : 'bg-emerald-400'}`} style={{ width: `${Math.min(100, p.quota_used_percent)}%` }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ApiUsagePanel;
